import { Box, Checkbox, FormControlLabel, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { KeyboardEvent, useRef, useMemo, useState, useEffect, useCallback } from "react";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { VirtualList } from "src/components";
import { Configuration } from "./components";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { buildLabelIndex, getConfigurationChipLabels, getMaxHierarchyDepth, getProductChipLabels, getProductDisplayName } from "src/pages/configurator/configurator-utils";
import {
  groupConfigurationsHierarchy,
  TConfigurationEntity,
  CONFIGURATION_NAME_FALLBACK_WIDTH,
  CONFIGURATION_NAME_WIDTH_VARIABLE,
  NAME_COLUMN_MAX_FRACTION,
  PRODUCT_NAME_FALLBACK_WIDTH,
  PRODUCT_NAME_WIDTH_VARIABLE,
  CONFIGURATION_ROW_OVERHEAD,
  PRODUCT_ROW_BASE_OVERHEAD,
  PRODUCT_ROW_DEPTH_INDENT
} from "./configurations-list-utils";
import { GroupNode, collectAllGroupNodePaths } from "../group-node";
import { TConfigurationSortField } from "../../configurator-types";
import { buildCanvasFont } from "src/utils/text-measure-utils";
import { useSharedColumnWidth } from "src/utils/hooks/useSharedColumnWidth";
import { CHIP_FONT_SIZE, CHIP_FONT_WEIGHT, estimateChipListWidth } from "src/components/chip-list/chip-list-utils";
import { VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID } from "../view-settings/view-settings-store";

// Mirrors products.tsx's getSortValue helper, scoped to the Configurations tab's two sortable fields
const getConfigurationSortValue = (configuration: TConfigurationEntity, field: TConfigurationSortField): string | number => {
  if (field === "productCount") return configuration.systemProductConfigurationItems?.length ?? 0;
  return configuration.systemProductConfigurationName ?? "";
};

const mapStateToProps = (state: RootState) => ({
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState,
  isLoading: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.isLoading,
  selectedConfigurations: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedConfigurations,
  searchTerm: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.searchTerm,
  labels: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels,
  // Configurations tab's own sort state, driving the order of both the flat list and the grouped buckets' contents
  configurationSortField: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationSortField,
  configurationSortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationSortDirection,
  groupFields: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupFields,
  groupSortBy: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupSortBy,
  groupSortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupSortDirection,
  expandCollapseSignal: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.expandCollapseSignal,
  viewSettings: state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied
});

const ownActions = {
  setSelectedConfigurations: configuratorActions.setSelectedConfigurations
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigurationsListBase = (props: Props) => {
  const {
    configurationsState,
    selectedConfigurations,
    setSelectedConfigurations,
    isLoading,
    searchTerm,
    labels,
    configurationSortField,
    configurationSortDirection,
    groupFields,
    groupSortBy,
    groupSortDirection,
    expandCollapseSignal,
    viewSettings,
  } = props;

  const theme = useTheme();
  const listRef = useRef<HTMLDivElement>(null);

  // tracks which group-tree paths the user has explicitly opened; empty set = all collapsed by default
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // reset to all-collapsed when the hierarchy changes — empty set needs no seeding, no post-render flash
  useEffect(() => {
    setExpandedPaths(new Set());
  }, [groupFields]);

  // Build a Map from entityId to labels for O(1) lookup during filtering
  const labelIndex = useMemo(() => buildLabelIndex(labels), [labels]);

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  // Search is the only narrowing this tab does. The Filter panel — attribute filter and custom
  // filters alike — is reachable from the Products tab alone, and its results live in that tab's own
  // productsState, so this list always renders the complete set of configurations.
  const filteredConfigurations = useMemo(() => {
    if (!configurationsState) return [];

    const term = searchTerm.trim().toLowerCase();
    if (!term) return configurationsState.configurations;

    return configurationsState.configurations.flatMap(configuration => {
      const nameMatches = configuration.systemProductConfigurationName?.toLowerCase().includes(term);
      // configuration-level labels — same match-anywhere-shows-whole-configuration rule as the name check
      const configLabels = labelIndex.get(configuration.systemProductConfigurationId ?? "") ?? [];
      const configLabelMatches = configLabels.some(label => label.shortname.toLowerCase().includes(term));
      // any matching item keeps the whole configuration — narrowing further to only the matching
      // items would orphan a matching child whose parent didn't match, silently dropping it from
      // the hierarchy tree
      const hasMatchingItem = (configuration.systemProductConfigurationItems || []).some(pli => {
        if (pli.productDescription?.toLowerCase().includes(term)) return true;
        if (pli.productNumber?.toLowerCase().includes(term)) return true;
        if (pli.productTypeName?.toLowerCase().includes(term)) return true;
        // systemProductConfigurationId is the item key in the list-view model
        const pliLabels = labelIndex.get(pli.systemProductConfigurationItemId ?? "") ?? [];
        return pliLabels.some(label => label.shortname.toLowerCase().includes(term));
      });
      return nameMatches || configLabelMatches || hasMatchingItem ? [configuration] : [];
    });
  }, [configurationsState, searchTerm, labelIndex]);

  // applies the Configurations tab's own sort (name or product count) on top of the filtered set,
  // independent of groupSortBy/groupSortDirection which only orders the group buckets themselves.
  // "none" (the default) skips sorting entirely, preserving the API's own most-recent-activity order.
  const sortedConfigurations = useMemo(() => {
    if (configurationSortField === "none") return filteredConfigurations;
    return [...filteredConfigurations].sort((a, b) => {
      const aVal = getConfigurationSortValue(a, configurationSortField);
      const bVal = getConfigurationSortValue(b, configurationSortField);
      const cmp = typeof aVal === "number" && typeof bVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
      return configurationSortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredConfigurations, configurationSortField, configurationSortDirection]);

  // only computed when grouped view is active — flat list uses sortedConfigurations directly
  const groups = useMemo(() => {
    if (groupFields.length === 0) return [];
    return groupConfigurationsHierarchy(sortedConfigurations, groupFields, labelIndex, configuratorPageT, groupSortBy, groupSortDirection);
  }, [sortedConfigurations, groupFields, groupSortBy, groupSortDirection, labelIndex, configuratorPageT]);

  // Both name columns resolve to one width per list so the chips that follow them line up down the
  // whole list. Measured from the data, not the rendered cells: the list is virtualized, so a
  // DOM-based measurement would make the column jump as rows recycle.
  const [listContainer, setListContainer] = useState<HTMLDivElement | null>(null);

  const configurationNames = useMemo(() => filteredConfigurations.map(configuration => configuration.systemProductConfigurationName ?? ""), [filteredConfigurations]);

  const productNames = useMemo(() => filteredConfigurations.flatMap(configuration => configuration.systemProductConfigurationItems ?? []).map(getProductDisplayName), [filteredConfigurations]);

  // 500 is the weight the two name cells override the theme's 600 with
  const configurationNameFont = useMemo(() => buildCanvasFont(500, theme.typography.body2.fontSize, theme.typography.fontFamily), [theme]);
  const productNameFont = useMemo(() => buildCanvasFont(500, theme.typography.subtitle2.fontSize, theme.typography.fontFamily), [theme]);
  const chipFont = useMemo(() => buildCanvasFont(CHIP_FONT_WEIGHT, CHIP_FONT_SIZE, theme.typography.fontFamily), [theme]);

  // Labels come first: the name column may only take what the most-loaded chip row leaves behind,
  // so switching labels on never costs a row its chips.
  const showsConfigurationLabels = viewSettings.selectedItemIds.includes(VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID);

  const widestConfigurationChipRow = useMemo(() => {
    if (!showsConfigurationLabels) return 0;
    return filteredConfigurations.reduce(
      (widest, configuration) => Math.max(widest, estimateChipListWidth(getConfigurationChipLabels(configuration.systemProductConfigurationId ?? undefined, labelIndex), chipFont)),
      0
    );
  }, [filteredConfigurations, labelIndex, chipFont, showsConfigurationLabels]);

  const productItems = useMemo(() => filteredConfigurations.flatMap(configuration => configuration.systemProductConfigurationItems ?? []), [filteredConfigurations]);

  const widestProductChipRow = useMemo(
    () => productItems.reduce((widest, item) => Math.max(widest, estimateChipListWidth(getProductChipLabels(item, labelIndex, viewSettings.selectedItemIds), chipFont)), 0),
    [productItems, labelIndex, viewSettings.selectedItemIds, chipFont]
  );

  // Deeper rows start further right, so the shared width has to fit the deepest one
  const maxProductDepth = useMemo(() => getMaxHierarchyDepth(productItems), [productItems]);

  const configurationNameWidth = useSharedColumnWidth(listContainer, configurationNames, configurationNameFont, {
    maxFraction: NAME_COLUMN_MAX_FRACTION,
    minWidth: CONFIGURATION_NAME_FALLBACK_WIDTH,
    // the cell's own ml={1}, plus slack against sub-pixel text metrics
    extraWidth: 10,
    reservedWidth: CONFIGURATION_ROW_OVERHEAD + widestConfigurationChipRow
  });

  const productNameWidth = useSharedColumnWidth(listContainer, productNames, productNameFont, {
    maxFraction: NAME_COLUMN_MAX_FRACTION,
    minWidth: PRODUCT_NAME_FALLBACK_WIDTH,
    extraWidth: 2,
    reservedWidth: PRODUCT_ROW_BASE_OVERHEAD + maxProductDepth * PRODUCT_ROW_DEPTH_INDENT + widestProductChipRow
  });

  const nameColumnSx = useMemo(
    () => ({
      [CONFIGURATION_NAME_WIDTH_VARIABLE]: `${configurationNameWidth ?? CONFIGURATION_NAME_FALLBACK_WIDTH}px`,
      [PRODUCT_NAME_WIDTH_VARIABLE]: `${productNameWidth ?? PRODUCT_NAME_FALLBACK_WIDTH}px`
    }),
    [configurationNameWidth, productNameWidth]
  );

  // Collapse All / Expand All, triggered from the header — requestId (not just the action) is the
  // dep so a repeated identical click still re-fires this even though the Set contents don't change
  useEffect(() => {
    if (!expandCollapseSignal) return;
    setExpandedPaths(expandCollapseSignal.action === "expand" ? new Set(collectAllGroupNodePaths(groups)) : new Set());
  }, [expandCollapseSignal?.requestId]);

  const handleListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const summaries = Array.from(listRef.current?.querySelectorAll<HTMLElement>(".MuiAccordionSummary-root") ?? []);
    const idx = summaries.indexOf(document.activeElement as HTMLElement);
    if (idx === -1) return;
    e.preventDefault();
    const next = e.key === "ArrowDown" ? summaries[idx + 1] : summaries[idx - 1];
    next?.focus();
  };

  const isAllSelected = useMemo(() => {
    if (configurationsState && filteredConfigurations.length > 0) return selectedConfigurations.length === filteredConfigurations.length;
    return false;
  }, [selectedConfigurations, configurationsState, filteredConfigurations]);

  // Rows receive their own isSelected boolean rather than subscribing to the selection array, so a
  // toggle re-renders one row instead of all of them. The Set keeps that lookup O(1) per row.
  const selectedConfigurationIdSet = useMemo(() => new Set(selectedConfigurations), [selectedConfigurations]);

  const renderConfiguration = useCallback(
    (myConfiguration: TConfigurationEntity) => (
      // systemProductConfigurationId replaces configurationId on the list-view model
      <Configuration configuration={myConfiguration} labelIndex={labelIndex} isSelected={selectedConfigurationIdSet.has(myConfiguration.systemProductConfigurationId ?? "")} />
    ),
    [labelIndex, selectedConfigurationIdSet]
  );

  if (isLoading) {
    return (
      <Stack gap={2}>
        {Array(4)
          .fill(null)
          .map((_, index) => {
            return <Skeleton key={index} height={40} />;
          })}
      </Stack>
    );
  }

  if (!isLoading && configurationsState)
    return (
      <Stack ref={setListContainer} flex={1} minHeight={0} overflow="hidden" sx={nameColumnSx}>
        {filteredConfigurations.length === 0 && (
          <Stack gap={2} mt={2}>
            <Typography variant="subtitle2" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.noConfigurations}
            </Typography>
            <Typography variant="subtitle1" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.getStartedByClickingNewConfiguration}
            </Typography>
          </Stack>
        )}

        {filteredConfigurations.length > 0 && (
          <Stack flex={1} minHeight={0} overflow="hidden">
            <Stack direction="row" alignItems="center" justifyContent="space-between" mx={1} flexShrink={0}>
              <FormControlLabel
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: theme.typography.subtitle1.fontSize,
                    fontWeight: "normal",
                    fontFamily: "AvenirNextLTPro, sans-serif"
                  }
                }}
                checked={isAllSelected}
                onChange={(_, checked) => {
                  // systemProductConfigurationId replaces configurationId on the list-view model
                  if (checked) setSelectedConfigurations(filteredConfigurations.map(x => x.systemProductConfigurationId?.toString() || ""));
                  else setSelectedConfigurations([]);
                }}
                control={<Checkbox title={genericTranslationsT.selectAll} size="small" />}
                label={genericTranslationsT.selectAll}
              />

              <Typography variant="subtitle1" color={theme.palette.customColors.darkGrey}>
                {filteredConfigurations.length} {filteredConfigurations.length > 1 ? configuratorPageT.configurations : configuratorPageT.configuration}
              </Typography>
            </Stack>
            {groupFields.length === 0 && (
              // Windowed — a project with hundreds of configurations mounted every accordion at once,
              // which dominated both first paint and every subsequent re-render
              <VirtualList
                items={sortedConfigurations}
                getItemKey={myConfiguration => myConfiguration.systemProductConfigurationId ?? ""}
                renderItem={renderConfiguration}
                estimateSize={48}
                gap={8}
                onKeyDown={handleListKeyDown}
                scrollRef={listRef}
              />
            )}

            {groupFields.length > 0 && (
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Stack gap={1}>
                  {groups.map(group => (
                    <GroupNode<TConfigurationEntity>
                      key={group.key}
                      node={group}
                      parentPath=""
                      expandedPaths={expandedPaths}
                      onToggle={(path, expanded) =>
                        setExpandedPaths(prev => {
                          const next = new Set(prev);
                          if (expanded) next.add(path);
                          else next.delete(path);
                          return next;
                        })
                      }
                      itemCountLabel={count => `${count} ${count !== 1 ? configuratorPageT.configurations : configuratorPageT.configuration}`}
                      renderItems={items => (
                        <Stack gap={1}>
                          {items.map(myConfiguration => (
                            // systemProductConfigurationId replaces configurationId on the list-view model
                            <Configuration
                              key={myConfiguration.systemProductConfigurationId}
                              configuration={myConfiguration}
                              labelIndex={labelIndex}
                              isSelected={selectedConfigurationIdSet.has(myConfiguration.systemProductConfigurationId ?? "")}
                            />
                          ))}
                        </Stack>
                      )}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    );
  return <></>;
};

export const ConfigurationsList = connect(mapStateToProps, ownActions)(ConfigurationsListBase);
